import { type CommodityItem } from 'core/domain/CommodityItem'
import { type Mode } from 'core/domain/Mode'
import { type Shipper } from 'core/domain/Shipper'
import { type StopType, type Stop } from 'core/domain/Stop'
import { type TruckType } from 'core/domain/Truck'
import { isNotEmpty } from 'core/utils/string'

import { type AgnosticShipment, type AgnosticShipmentStatus } from '../domain/AgnosticShipment'
import { type Driver, type LastLocation, type ShipmentIdentifier } from '../domain/BaseShipment'

export type RawDriver = {
  carrier: string
  email: string | null
  name: string | null
  phone_number: string | null
  roles: Array<string> | null
  uuid: string
}

type RawItem = {
  po_number?: string | null
  so_number?: string | null
  commodity: string | null
  height?: string | null
  length?: string | null
  weight?: string | null
  width?: string | null
}

type RawLastLocation = {
  city: string
  entered_by: string | null
  latitude: string
  longitude: string
  source: string
  state: string
  timestamp: string
  uuid: string
}

type RawShipper = {
  company_name: string
  email: string
  logo_email_url: string | null
  phone_number: string | null
  business: string | null
}

type RawAgnosticStopCheck = {
  check_in: {
    done_at: string
  } | null
  check_out: {
    done_at: string
  } | null
}

type RawStop = {
  id: number
  city: string
  country: string
  date: string | null
  date_source?: string | null
  state: string
  zipcode: string
  stop_type?: StopType
  stop_index?: number
} & RawAgnosticStopCheck

export type RawAgnosticShipment = {
  bol_number: string | null
  cancel_reason: string | null
  canceled_at: string | null
  canceled_by: string | null
  carrier_ref_numbers: string[] | null
  carrier_update_request_sent_at: string | null
  dispatcher: string | null
  carrier_1xp_link: string
  driver: RawDriver | null
  equipment_length: string | null
  equipment_type: string
  hazmat: boolean
  is_loadsmart_carrier: boolean
  items: Array<RawItem>
  last_location?: RawLastLocation | null
  mileage: number
  mode: string
  po_numbers: string[] | null
  shipper: RawShipper
  shipper_guide_id: string
  shipper_ref_number: string | null
  so_number: string
  status: string
  stops: Array<RawStop>
  tracking_link: string
  tractor_number: string | null
  uuid: string
}

export function mapApiToItems(items: Array<RawItem>): Array<CommodityItem> {
  return items.map(item => ({
    commodity: item.commodity ?? undefined,
    height: item.height ? Number(item.height) : undefined,
    length: item.length ? Number(item.length) : undefined,
    weight: item.weight ? Number(item.weight) : undefined,
    width: item.width ? Number(item.width) : undefined,
  }))
}

export function mapApiToDriver(driver: RawDriver | null): Driver | undefined {
  if (!driver) return undefined

  const { carrier, email, name, phone_number, roles, uuid } = driver
  return {
    carrier,
    email: email ?? undefined,
    name: name ?? undefined,
    phoneNumber: phone_number ?? undefined,
    roles: roles ?? undefined,
    uuid: uuid,
  }
}

export function mapApiToIdentifiers(shipment: RawAgnosticShipment): Array<ShipmentIdentifier> {
  const { bol_number, po_numbers, so_number, shipper_ref_number } = shipment

  let identifiers: Array<ShipmentIdentifier> = []

  if (po_numbers) identifiers.push({ label: 'PO', values: po_numbers })
  if (bol_number) identifiers.push({ label: 'BOL', values: [bol_number] })
  if (so_number) identifiers.push({ label: 'SO', values: so_number?.split(',') })
  if (shipper_ref_number) identifiers.push({ label: 'REF', values: [String(shipper_ref_number)] })

  return identifiers
}

export function mapApiToLastLocation(
  lastLocation?: RawLastLocation | null
): LastLocation | undefined {
  if (!lastLocation) return undefined

  return {
    city: lastLocation.city,
    enteredBy: lastLocation.entered_by ?? undefined,
    latitude: lastLocation.latitude,
    longitude: lastLocation.longitude,
    source: lastLocation.source,
    state: lastLocation.state,
    timestamp: new Date(lastLocation.timestamp),
    uuid: lastLocation.uuid,
  }
}

export function mapApiToShipper(shipper: RawShipper): Shipper {
  const { company_name, email, logo_email_url, phone_number } = shipper
  return {
    companyName: company_name,
    email: email,
    logoUrl: logo_email_url && isNotEmpty(logo_email_url) ? logo_email_url : undefined,
    phoneNumber: phone_number && isNotEmpty(phone_number) ? phone_number : undefined,
  }
}

export function mapApiToStopChecks(stop: RawStop) {
  let checkedInAt
  let checkedOutAt
  const { check_in, check_out } = stop

  if (check_in) checkedInAt = new Date(check_in.done_at)
  if (check_out) checkedOutAt = new Date(check_out.done_at)

  return {
    checkedInAt,
    checkedOutAt,
  }
}

export function mapApiToStops(stops: Array<RawStop>): Array<Stop> {
  return stops.map(stop => ({
    city: stop.city,
    country: stop.country,
    date: stop.date ?? undefined,
    state: stop.state,
    zipCode: stop.zipcode,
    ...mapApiToStopChecks(stop),
  }))
}

export function mapApiToAgnosticShipment(shipment: RawAgnosticShipment): AgnosticShipment {
  const {
    carrier_1xp_link,
    driver,
    items,
    last_location,
    mileage,
    mode,
    shipper,
    stops,
    status,
    tractor_number,
    uuid,
  } = shipment

  const parsedDriver = mapApiToDriver(driver)
  const identifiers = mapApiToIdentifiers(shipment)
  const lastLocation = mapApiToLastLocation(last_location)
  const parsedItems = mapApiToItems(items)
  const parsedShipper = mapApiToShipper(shipper)
  const parsedStops = mapApiToStops(stops)

  return {
    equipmentType: shipment.equipment_type as TruckType,
    shipmentURL: carrier_1xp_link,
    driver: parsedDriver,
    identifiers,
    items: parsedItems,
    mileage,
    lastLocation,
    mode: mode as Mode,
    status: status as AgnosticShipmentStatus,
    shipper: parsedShipper,
    stops: parsedStops,
    tractorNumber: tractor_number || undefined,
    uuid,
  }
}
